/**
 * LAGOSEntityReporting — Phase 1 Priority #1
 * Entity Onboarding (Manual + Bulk) + Registry
 * /state/lagos/reporting
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLagos } from "@/context/LAGOSContext";
import { PageHeader, SectionCard } from "@/components/ds";
import { toast } from "sonner";
import client from "@/api/client";
import { Upload, Save, RefreshCw, Search, Filter, ChevronLeft, ChevronRight, X } from "lucide-react";

const fmtN = (v, u="") => { if (!v && v!==0) return "—"; const n=Number(v); return n>=1000 ? `${(n/1000).toFixed(0)}K ${u}`.trim() : `${n.toLocaleString()} ${u}`.trim(); };
const LABEL_C = { Platinum:"#7c3aed",Gold:"#d97706",Silver:"#64748b",Bronze:"#b45309",Emerging:"#94a3b8" };

const SECTORS = ["plastic_recycling","metal_recycling","paper_recycling","food_processing","light_manufacturing","packaging","rice_mill","cassava_processing","poultry","waste_collection","landfill_diversion","composting","solar_energy","mini_grid","fleet_operator","distribution","reuse_platform","repair_business","agro_processing"];
const LGAS = ["Alimosho","Ikeja","Eti-Osa","Surulere","Apapa","Oshodi-Isolo","Mushin","Ikorodu","Badagry","Agege"];
const TYPES = ["SME","Recycler","Project","Waste Operator","Energy Provider","Transport","Agriculture","Circular Economy"];
const EV_LEVELS = ["low","medium","high","measured","estimated","declared"];

const STEP_LABELS = ["1. Identity","2. Sector","3. Activity","4. Circularity","5. Evidence","6. Review"];

export default function LAGOSEntityReporting() {
  const { selectedPeriod, periods, setSelectedPeriod, refresh } = useLagos();
  const navigate = useNavigate();
  const [tab, setTab] = useState("registry");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState({});

  // Registry state
  const [entities, setEntities] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ sector:"", lga:"", label:"", search:"" });
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    client.get("/lagos/sector-templates").then(r => setTemplates(r.data || {})).catch(()=>{});
  }, []);

  const loadEntities = useCallback(async () => {
    if (!selectedPeriod) return;
    setLoadingList(true);
    try {
      const params = new URLSearchParams({ page, per_page: 50, ...Object.fromEntries(Object.entries(filters).filter(([,v])=>v)) });
      const r = await client.get(`/lagos/entities/${selectedPeriod}?${params}`);
      setEntities(r.data.entities || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    } catch { toast.error("Failed to load entities"); }
    finally { setLoadingList(false); }
  }, [selectedPeriod, page, filters]);

  useEffect(() => { if (tab === "registry") loadEntities(); }, [tab, page, selectedPeriod, loadEntities]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.sector || !form.lga || !selectedPeriod) {
      toast.error("Name, Sector, and LGA are required");
      return;
    }
    setSaving(true);
    const eid = "ENT-" + form.lga.toUpperCase().slice(0,3) + "-" + Date.now().toString().slice(-6);
    try {
      await client.post(`/lagos/entity/${eid}/${selectedPeriod}`, form);
      toast.success(`${form.name} onboarded and calculated`);
      setForm({}); setStep(0);
      refresh();
      setTab("registry"); loadEntities();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await client.post("/lagos/parse-upload", fd, { headers: {"Content-Type":"multipart/form-data"} });
      const parsed = r.data?.result?.entities || [];
      if (parsed.length === 0) { toast.error("No valid entities found"); return; }
      const res = await client.post(`/lagos/batch-calculate/${selectedPeriod}`, { entities: parsed });
      toast.success(`${res.data.processed} entities imported`);
      refresh(); loadEntities();
    } catch { toast.error("Upload failed"); }
  };

  const sectorTemplate = templates[form.sector] || {};
  const keyInputs = sectorTemplate.key_inputs || ["electricity_kwh","diesel_litres","production_output"];

  const INPUT_LABELS = {
    electricity_kwh:"Electricity (kWh/yr)", diesel_litres:"Diesel (litres/yr)",
    petrol_litres:"Petrol (litres/yr)", gas_kg:"LPG/Gas (kg/yr)",
    production_output:`Production Output (${sectorTemplate.unit||"tonnes"}/yr)`,
    waste_generated_t:"Waste Generated (t/yr)", waste_recycled_t:"Waste Recycled (t/yr)",
    material_input_t:"Material Inputs (t/yr)", transport_km:"Transport (km/yr)",
    employees:"Employees",
  };

  const StepContent = () => {
    if (step === 0) return (
      <div className="space-y-3">
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Business / Entity Name *</label>
          <input value={form.name||""} onChange={e=>setF("name",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400" placeholder="e.g. Apapa Plastic Recovery Ltd" data-testid="entity-name" /></div>
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Entity Type *</label>
          <select value={form.type||""} onChange={e=>setF("type",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
            <option value="">— Select type —</option>
            {TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-slate-500 mb-1">LGA *</label>
            <select value={form.lga||""} onChange={e=>setF("lga",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
              <option value="">— Select LGA —</option>
              {LGAS.map(l=><option key={l}>{l}</option>)}</select></div>
          <div><label className="block text-xs font-medium text-slate-500 mb-1">Sub-location</label>
            <input value={form.sub_sector||""} onChange={e=>setF("sub_sector",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" placeholder="e.g. Mile 2 Industrial" /></div>
        </div>
      </div>
    );
    if (step === 1) return (
      <div className="space-y-3">
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Primary Sector *</label>
          <select value={form.sector||""} onChange={e=>setF("sector",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white" data-testid="sector-select">
            <option value="">— Select sector —</option>
            {SECTORS.map(s=><option key={s} value={s}>{s.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</option>)}</select></div>
        {form.sector && <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
          <strong>Sector template active:</strong> {(templates[form.sector]||{}).label || form.sector}. Key inputs: {((templates[form.sector]||{}).key_inputs||[]).join(", ")}
        </div>}
      </div>
    );
    if (step === 2) return (
      <div className="space-y-3" data-testid="activity-inputs">
        {keyInputs.map(k => (
          <div key={k}><label className="block text-xs font-medium text-slate-500 mb-1">{INPUT_LABELS[k]||k}</label>
            <input type="number" min="0" value={form[k]||""} onChange={e=>setF(k,e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
              placeholder="0" data-testid={`input-${k}`} />
          </div>
        ))}
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Employees</label>
          <input type="number" min="0" value={form.employees||""} onChange={e=>setF("employees",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" /></div>
      </div>
    );
    if (step === 3) return (
      <div className="space-y-3">
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Recycling Rate (%)</label>
          <input type="number" min="0" max="100" value={form.recycling_rate_pct||""} onChange={e=>setF("recycling_rate_pct",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" /></div>
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Reuse Rate (%)</label>
          <input type="number" min="0" max="100" value={form.reuse_rate_pct||""} onChange={e=>setF("reuse_rate_pct",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" /></div>
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Waste Recycled (t/yr)</label>
          <input type="number" min="0" value={form.waste_recycled_t||""} onChange={e=>setF("waste_recycled_t",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" /></div>
      </div>
    );
    if (step === 4) return (
      <div className="space-y-3">
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Data Source</label>
          <select value={form.data_source||"estimated"} onChange={e=>setF("data_source",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
            <option value="measured">Measured (calibrated meter/weigh-bridge)</option>
            <option value="estimated">Estimated (engineering estimate)</option>
            <option value="declared">Declared (self-reported, unverified)</option>
          </select></div>
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Evidence Level</label>
          <select value={form.evidence_level||"low"} onChange={e=>setF("evidence_level",e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
            {EV_LEVELS.map(l=><option key={l} value={l}>{l.charAt(0).toUpperCase()+l.slice(1)}</option>)}</select></div>
        <div><label className="block text-xs font-medium text-slate-500 mb-1">Notes / Comments</label>
          <textarea value={form.notes||""} onChange={e=>setF("notes",e.target.value)} rows={3} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" placeholder="Context, data gaps, plans..." /></div>
      </div>
    );
    // Review step
    return (
      <div className="space-y-2 text-xs">
        <p className="font-bold text-slate-700 mb-2">Review before submitting:</p>
        {[["Name",form.name],["Type",form.type],["Sector",(form.sector||"").replace(/_/g," ")],["LGA",form.lga],
          ["Electricity",`${form.electricity_kwh||0} kWh/yr`],["Diesel",`${form.diesel_litres||0} L/yr`],
          ["Production",`${form.production_output||0} ${sectorTemplate.unit||"t"}/yr`],
          ["Recycled",`${form.waste_recycled_t||0} t/yr`],
          ["Evidence Level",form.evidence_level]].map(([k,v])=>(
          <div key={k} className="flex justify-between py-1 border-b border-slate-50">
            <span className="text-slate-400">{k}</span><span className="font-medium">{v||"—"}</span>
          </div>
        ))}
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-xs">
          Eco-label and carbon pathway will be automatically calculated and assigned on save.
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <PageHeader label="ENTITY REPORTING & ONBOARDING" title="" />
          <p className="text-xs text-slate-400 -mt-3">Core ingestion engine · Manual onboarding · Bulk upload · SSOT write</p>
        </div>
        <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
          {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
        </select>
      </div>

      <div className="flex gap-2">
        {[["registry","Entity Registry"],["onboard","Manual Onboarding"],["upload","Bulk Upload"]].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} data-testid={`tab-${t}`}
            className={`text-xs font-semibold px-4 py-2 rounded-lg border transition-all ${tab===t?"bg-[#1d4ed8] text-white border-[#1d4ed8]":"bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>{l}</button>
        ))}
        <span className="ml-auto text-xs text-slate-400 self-center">{total.toLocaleString()} entities · Page {page}/{pages}</span>
      </div>

      {tab === "upload" && (
        <SectionCard title="Bulk Entity Upload (CSV/Excel)">
          <div className="p-6 text-center space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-10">
              <Upload size={28} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500 mb-3">CSV with columns: name, sector, lga, electricity_kwh, diesel_litres, waste_recycled_t, employees, evidence_level</p>
              <label className="cursor-pointer bg-[#1d4ed8] text-white text-xs px-4 py-2 rounded-lg">
                Browse Files <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleUpload} data-testid="bulk-upload-input" />
              </label>
            </div>
            <p className="text-xs text-slate-400">Supports up to 500 rows per upload. Invalid rows are skipped. All valid rows are calculated immediately.</p>
          </div>
        </SectionCard>
      )}

      {tab === "onboard" && (
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <SectionCard title={`Step ${step+1} of 6 — ${STEP_LABELS[step]}`}>
              <div className="p-5">
                {/* Progress */}
                <div className="flex gap-1 mb-5">
                  {STEP_LABELS.map((_,i)=>(
                    <div key={i} className={`flex-1 h-1.5 rounded-full ${i<=step?"bg-[#1d4ed8]":"bg-slate-100"}`} />
                  ))}
                </div>
                <StepContent />
                <div className="flex gap-2 mt-5">
                  {step > 0 && <button onClick={()=>setStep(s=>s-1)} className="flex items-center gap-1 text-xs border border-slate-200 rounded-lg px-3 py-2 text-slate-500 hover:bg-slate-50"><ChevronLeft size={12} /> Back</button>}
                  {step < 5
                    ? <button onClick={()=>setStep(s=>s+1)} className="flex-1 flex items-center justify-center gap-1 bg-[#1d4ed8] text-white text-xs rounded-lg px-4 py-2" data-testid="step-next">Next <ChevronRight size={12} /></button>
                    : <button onClick={handleSave} disabled={saving} className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 text-white text-xs rounded-lg px-4 py-2 disabled:opacity-60" data-testid="save-entity">
                        {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                        {saving ? "Calculating…" : "Save & Calculate"}
                      </button>}
                </div>
              </div>
            </SectionCard>
          </div>
          <div className="space-y-3">
            <SectionCard title="SSOT Preview">
              <div className="p-4 text-xs space-y-1">
                {[["Name",form.name],["Sector",(form.sector||"").replace(/_/g," ")],["LGA",form.lga],["Evidence",form.evidence_level]].map(([k,v])=>(
                  <div key={k} className="flex justify-between py-1 border-b border-slate-50">
                    <span className="text-slate-400">{k}</span><span className="font-medium">{v||"—"}</span>
                  </div>
                ))}
                <div className="pt-2 text-[10px] text-slate-400">Eco-label, emissions, and carbon pathway auto-calculated on save.</div>
              </div>
            </SectionCard>
          </div>
        </div>
      )}

      {tab === "registry" && (
        <SectionCard title="Entity Registry">
          {/* Filters */}
          <div className="px-4 pt-4 pb-3 flex gap-2 flex-wrap border-b border-slate-100">
            <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-xs text-slate-400">
              <Search size={10} /><input value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))}
                placeholder="Search entities…" className="outline-none text-slate-700 ml-1 w-36" data-testid="entity-search" />
            </div>
            {[["sector",SECTORS,"Sector"],["lga",LGAS,"LGA"],["label",["Platinum","Gold","Silver","Bronze","Emerging"],"Label"]].map(([key,opts,placeholder])=>(
              <select key={key} value={filters[key]} onChange={e=>setFilters(f=>({...f,[key]:e.target.value}))}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600">
                <option value="">All {placeholder}s</option>
                {opts.map(o=><option key={o} value={o}>{typeof o==="string"?o.replace(/_/g," "):o}</option>)}
              </select>
            ))}
            <button onClick={()=>setFilters({sector:"",lga:"",label:"",search:""})} className="text-xs text-blue-500 flex items-center gap-1"><X size={10} /> Clear</button>
            <button onClick={loadEntities} className="ml-auto text-xs bg-[#1d4ed8] text-white px-3 py-1.5 rounded-lg" data-testid="apply-filters">Apply</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-100">
                {["Entity","Type","Sector","LGA","tCO₂e","Reduction","Label","Pathway","Score","Action"].map(h=>(
                  <th key={h} className="text-left py-2 px-3 text-slate-400 font-medium">{h}</th>))}
              </tr></thead>
              <tbody>
                {loadingList ? <tr><td colSpan={10} className="py-8 text-center text-slate-400 text-xs">Loading…</td></tr>
                : entities.map(e=>(
                  <tr key={e.entity_id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium max-w-[160px] truncate text-slate-800">{e.name}</td>
                    <td className="py-2 px-3 text-slate-500">{e.type}</td>
                    <td className="py-2 px-3 text-slate-500 capitalize">{(e.sector||"").replace(/_/g," ")}</td>
                    <td className="py-2 px-3 text-slate-500">{e.lga}</td>
                    <td className="py-2 px-3 text-slate-700">{fmtN(e.total_tco2e_yr,"t")}</td>
                    <td className="py-2 px-3 text-emerald-700 font-bold">{fmtN(e.reduction_potential_tco2e,"t")}</td>
                    <td className="py-2 px-3"><span className="text-[10px] font-bold" style={{color:LABEL_C[e.eco_label]}}>{e.eco_label}</span></td>
                    <td className="py-2 px-3"><span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-50 text-blue-700 capitalize">{e.carbon_pathway}</span></td>
                    <td className="py-2 px-3 font-bold text-slate-700">{(e.composite_score||0).toFixed(0)}</td>
                    <td className="py-2 px-3">
                      <button onClick={()=>navigate(`/state/lagos/entity/${e.entity_id}`)}
                        className="text-[10px] text-blue-500 hover:underline" data-testid={`view-${e.entity_id}`}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-xs text-slate-400">Showing {entities.length} of {total} entities</p>
            <div className="flex gap-2">
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="text-xs border border-slate-200 rounded px-2 py-1 disabled:opacity-40"><ChevronLeft size={10} /></button>
              <span className="text-xs text-slate-500">Page {page} of {pages}</span>
              <button onClick={()=>setPage(p=>Math.min(pages,p+1))} disabled={page===pages} className="text-xs border border-slate-200 rounded px-2 py-1 disabled:opacity-40"><ChevronRight size={10} /></button>
            </div>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
